import { Link } from 'react-router-dom';
import { 
  Crown, TrendingUp, Users, BarChart3, Target,
  CheckCircle, ArrowRight, Phone, ChevronRight, Zap
} from 'lucide-react';
import SEO from '../components/shared/SEO';

// Dispatch Plan Card
const PlanCard = ({ tier, price, period, features, highlight, color }) => (
  <div 
    className={`relative bg-[#121212] border ${highlight ? 'border-[#D4AF37]' : 'border-white/10'} p-8 flex flex-col`}
    data-testid={`grow-plan-${tier.toLowerCase()}`}
  >
    {highlight && (
      <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-4 py-1 bg-[#D4AF37] text-black text-xs font-bold uppercase tracking-wider">
        Most Popular
      </div>
    )}
    
    <div className="mb-6">
      <span 
        className="text-xs font-bold uppercase tracking-widest"
        style={{ color }}
      >
        {tier}
      </span>
      <div className="flex items-baseline gap-1 mt-2">
        <span className="text-4xl font-bold text-white">${price}</span>
        <span className="text-white/50">/{period}</span>
      </div>
    </div>
    
    <ul className="space-y-3 flex-1 mb-8">
      {features.map((feature, i) => (
        <li key={i} className="flex items-start gap-3 text-white/70 text-sm">
          <CheckCircle className="w-5 h-5 flex-shrink-0" style={{ color }} />
          <span>{feature}</span>
        </li>
      ))}
    </ul>
    
    <Link
      to="/contact"
      className={`w-full py-4 text-center font-bold uppercase tracking-wider transition-colors ${
        highlight 
          ? 'bg-[#D4AF37] text-black hover:bg-[#F5D47B]' 
          : 'border border-white/20 text-white hover:bg-white/5'
      }`}
    >
      Schedule Strategy Call
    </Link>
  </div>
);

// Feature Card
const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="bg-[#121212] border border-white/5 p-6">
    <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center mb-4">
      <Icon className="w-6 h-6 text-[#D4AF37]" />
    </div>
    <h4 className="text-white font-bold text-lg mb-2">{title}</h4>
    <p className="text-white/50 text-sm leading-relaxed">{description}</p>
  </div>
);

const GrowPage = () => {
  const plans = [
    {
      tier: 'Bronze',
      price: '300',
      period: 'week',
      color: '#CD7F32',
      features: [
        'Dedicated dispatcher (Mon-Fri)',
        'Load search & booking',
        'Rate confirmations & carrier packets',
        'Basic route planning',
        'Pickup/delivery coordination',
        'Weekly load summary',
      ],
    },
    {
      tier: 'Silver',
      price: '400',
      period: 'week',
      color: '#C0C0C0',
      highlight: true,
      features: [
        'Everything in Bronze, plus:',
        'Extended hours support',
        'Active rate negotiation',
        'RPM optimization strategy',
        'Multi-stop planning',
        'After-hours text support',
        'Weekly KPI reporting',
      ],
    },
    {
      tier: 'Gold',
      price: '500',
      period: 'week',
      color: '#D4AF37',
      features: [
        'Everything in Silver, plus:',
        'Priority load access',
        'Lane strategy optimization',
        'Rate trend tracking',
        'Accessorial request support',
        'Broker relationship management',
        'Monthly performance reviews',
      ],
    },
  ];

  const dispatchFeatures = [
    {
      icon: TrendingUp,
      title: 'RPM Optimization',
      description: 'Strategic load selection focused on maximizing your revenue per mile, not just filling your trailer.',
    },
    {
      icon: Users,
      title: 'Broker Relationship Management',
      description: 'Building and maintaining strong broker relationships that lead to better rates and preferred freight access.',
    },
    {
      icon: Target,
      title: 'Load Planning Strategy',
      description: 'Strategic load sequencing and route optimization to minimize deadhead miles and maximize productivity.',
    },
    {
      icon: BarChart3,
      title: 'Revenue Performance Tracking',
      description: 'Detailed performance metrics and reporting to track your growth and identify improvement opportunities.',
    },
    {
      icon: Zap,
      title: 'Scalable Dispatch Systems',
      description: 'Infrastructure built to grow with you — from single truck to fleet operations.',
    },
    {
      icon: Crown,
      title: 'Premium Service Access',
      description: 'Priority support, dedicated dispatchers, and white-glove service for serious operators.',
    },
  ];

  return (
    <div data-testid="grow-page" className="bg-[#0A0A0A]">
      <SEO 
        title="GROW - Dispatch Services | Queen B Logistics"
        description="Revenue-focused dispatch operations built for scale. RPM optimization, broker management, load planning strategy, and performance tracking."
        canonical="https://queenblogistics.com/grow"
        keywords="freight dispatch, owner operator dispatch, trucking dispatch services, load booking, RPM optimization, broker management"
      />

      {/* Hero Section */}
      <section className="py-20 md:py-32 bg-gradient-to-b from-black to-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-4xl">
            {/* Breadcrumb */}
            <div className="flex items-center gap-2 text-sm mb-8">
              <Link to="/" className="text-white/50 hover:text-white">Home</Link>
              <ChevronRight className="w-4 h-4 text-white/30" />
              <span className="text-[#D4AF37]">Grow</span>
            </div>

            {/* Title Block */}
            <div className="flex items-center gap-4 mb-6">
              <div className="w-16 h-16 bg-[#D4AF37]/10 flex items-center justify-center border border-[#D4AF37]/30">
                <Crown className="w-8 h-8 text-[#D4AF37]" />
              </div>
              <div>
                <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
                  Grow Your Business
                </span>
                <h1 className="font-['Barlow_Condensed'] text-4xl md:text-5xl lg:text-6xl font-bold text-white uppercase">
                  Dispatch Services
                </h1>
              </div>
            </div>

            <p className="text-xl md:text-2xl text-white/80 font-medium mb-6">
              Revenue-focused dispatch operations built for scale.
            </p>
            
            <p className="text-white/50 text-lg leading-relaxed max-w-2xl mb-10">
              We don't just book loads — we build revenue systems. 
              Our dispatch structure focuses on RPM strategy, broker management, 
              and consistent performance tracking to grow your business sustainably.
            </p>

            <Link
              to="/intake"
              className="inline-flex items-center gap-2 px-8 py-4 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
              data-testid="grow-cta"
            >
              Start Dispatching
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
              Our Approach
            </span>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-4">
              What We Focus On
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {dispatchFeatures.map((feature, i) => (
              <FeatureCard key={i} {...feature} />
            ))}
          </div>
        </div>
      </section>

      {/* Differentiator Section */}
      <section className="py-16 bg-[#D4AF37]/10 border-y border-[#D4AF37]/30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row items-start gap-6">
            <div className="w-16 h-16 bg-[#D4AF37]/20 flex items-center justify-center flex-shrink-0">
              <Crown className="w-8 h-8 text-[#D4AF37]" />
            </div>
            <div>
              <h3 className="text-[#D4AF37] font-bold text-xl mb-2">
                We Build Revenue Systems, Not Just Book Loads
              </h3>
              <p className="text-white/70 leading-relaxed">
                Anyone can post you on a load board. Our approach is different — we focus on 
                strategic lane optimization, broker relationship development, and consistent 
                revenue performance. This is infrastructure, not guesswork.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="plans" className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="text-center mb-16">
            <span className="text-[#D4AF37] text-sm font-bold uppercase tracking-widest">
              Pricing Plans
            </span>
            <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mt-2 mb-4">
              Choose Your Dispatch Level
            </h2>
            <div className="w-24 h-1 bg-[#D4AF37] mx-auto mb-6" />
            <p className="text-white/50 max-w-2xl mx-auto">
              Weekly billing with no long-term contracts. 
              Quality service that earns your trust every week.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {plans.map((plan) => (
              <PlanCard key={plan.tier} {...plan} />
            ))}
          </div>

          {/* Bundle Notice */}
          <div className="mt-12 max-w-3xl mx-auto">
            <div className="bg-[#D4AF37]/10 border border-[#D4AF37]/30 p-6 text-center">
              <h4 className="text-[#D4AF37] font-bold text-lg mb-2">
                Bundle & Save 20%
              </h4>
              <p className="text-white/70 mb-4">
                Combine Compliance + Dispatch services and save 20% on your total cost. 
                Get full lifecycle coverage with Protect + Grow.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#F5D47B] transition-colors font-medium"
              >
                Learn About Bundle Pricing
                <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Requirements Section */}
      <section className="py-20 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl mx-auto">
            <h2 className="font-['Barlow_Condensed'] text-3xl md:text-4xl font-bold text-white uppercase mb-8 text-center">
              Requirements
            </h2>
            
            <div className="bg-[#121212] border border-white/10 p-8">
              <ul className="space-y-4">
                {[
                  'Active MC and DOT authority',
                  'Current insurance coverage (liability, cargo, physical damage)',
                  'Valid W9 documentation',
                  'Professional communication standards',
                  'Commitment to on-time performance',
                ].map((req, i) => (
                  <li key={i} className="flex items-start gap-3 text-white/70">
                    <CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <span>{req}</span>
                  </li>
                ))}
              </ul>
              
              <div className="mt-6 pt-6 border-t border-white/10">
                <p className="text-white/50 text-sm">
                  <strong className="text-[#D4AF37]">Don't have authority yet?</strong>{' '}
                  Start with our <Link to="/build" className="text-[#D4AF37] hover:underline">Build program</Link> to get your MC number and launch properly.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-[#0A0A0A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-['Barlow_Condensed'] text-4xl md:text-5xl font-bold text-white uppercase mb-6">
            Ready to <span className="text-[#D4AF37]">Grow?</span>
          </h2>
          <p className="text-white/60 text-lg mb-10 max-w-2xl mx-auto">
            Schedule a strategy call to discuss your dispatch needs and find the right plan for your operation.
          </p>
          <Link
            to="/contact"
            className="inline-flex items-center gap-2 px-10 py-5 bg-[#D4AF37] text-black font-bold uppercase tracking-wider hover:bg-[#F5D47B] transition-colors"
            data-testid="grow-final-cta"
          >
            <Phone className="w-5 h-5" />
            Schedule Strategy Call
          </Link>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="py-8 bg-[#050505] border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <p className="text-white/30 text-xs text-center">
            Dispatch services require active MC authority and valid insurance. 
            Queen B Logistics operates as a dispatch service provider, not a broker or carrier.
          </p>
        </div>
      </section>
    </div>
  );
};

export default GrowPage;
